import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '../common/Modal';
import { motion } from 'framer-motion';
import { Building2, Calendar, ArrowUpCircle, CheckCircle, XCircle, Trash2, Plus, Minus, AlertCircle, Info } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { UpgradeRequest } from '../../types/upgrade';
import { useUpgradeStore } from '../../stores/upgradeStore';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  requests: UpgradeRequest[];
};

type RequestGroup = {
  key: string;
  hotel: string;
  period: string;
  startDate?: string;
  endDate?: string;
  requests: UpgradeRequest[];
  totalRequests: number;
  approved: number;
  price: number;
  maxApprovals: number;
  similarRequests?: {
    count: number;
    avgPrice: number;
  } | null;
  fromRoomType?: string; // Added to track from room type
  toRoomType?: string;   // Added to track to room type
};

export const BatchApprovalModal = ({ isOpen, onClose, requests }: Props) => {
  const { translations } = useLanguage();
  const [requestGroups, setRequestGroups] = useState<RequestGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGrouping, setIsGrouping] = useState(false);
  const { approveRequest, rejectRequest, requests: allRequests } = useUpgradeStore();

  // Group and analyze requests
  useEffect(() => {
    if (isOpen) {
      setIsGrouping(true);
      // Only consider pending requests
      const pendingRequests = requests.filter(r => r.status === 'pending');
      
      // Group by hotel, date period, AND room type conversion
      const groups: Record<string, RequestGroup> = {};
      
      pendingRequests.forEach(request => {
        if (!request.fromHotel) return;
        
        const period = request.startDate && request.endDate 
          ? `${request.startDate} - ${request.endDate}` 
          : 'No period';
        
        // Include room type conversion in the key
        const key = `${request.fromHotel}|${period}|${request.fromRoomType}|${request.toRoomType}`;
        
        if (!groups[key]) {
          groups[key] = {
            key,
            hotel: request.fromHotel,
            period,
            startDate: request.startDate,
            endDate: request.endDate,
            fromRoomType: request.fromRoomType, // Store from room type
            toRoomType: request.toRoomType,     // Store to room type
            requests: [],
            totalRequests: 0,
            approved: 0,
            price: 0,
            maxApprovals: 0,
            similarRequests: null
          };
        }
        
        groups[key].requests.push(request);
        groups[key].totalRequests++;
      });
      
      // Find similar previous requests for each group
      Object.values(groups).forEach(group => {
        const similarRequests = findSimilarRequests(
          allRequests, 
          group.hotel, 
          group.fromRoomType, // Pass from room type
          group.toRoomType,   // Pass to room type
          group.startDate, 
          group.endDate
        );
        
        group.similarRequests = similarRequests;
        
        // Set initial price based on similar requests if they exist
        if (similarRequests && similarRequests.count > 0) {
          group.price = Math.round(similarRequests.avgPrice);
        }
      });
      
      // Convert groups to array and sort by hotel and period
      const groupArray = Object.values(groups).sort((a, b) => {
        if (a.hotel !== b.hotel) return a.hotel.localeCompare(b.hotel);
        return a.period.localeCompare(b.period);
      });
      
      // Set default maxApprovals to total number of requests in each group
      groupArray.forEach(group => {
        group.maxApprovals = group.totalRequests;
      });
      
      setRequestGroups(groupArray);
      setIsGrouping(false);
    }
  }, [isOpen, requests, allRequests]);

  // Find similar previous requests for the same hotel, period, and room type conversion
  const findSimilarRequests = (
    allRequests: Record<string, UpgradeRequest>, 
    hotel: string, 
    fromRoomType?: string, 
    toRoomType?: string, 
    startDate?: string, 
    endDate?: string
  ) => {
    // Filter for approved, payment_pending, or paid requests for the same hotel and room type conversion
    const similarRequests = Object.values(allRequests).filter(req => 
      (req.status === 'approved' || req.status === 'payment_pending' || req.status === 'paid' || req.status === 'completed') &&
      req.fromHotel === hotel &&
      req.fromRoomType === fromRoomType && // Match from room type
      req.toRoomType === toRoomType &&     // Match to room type
      req.price && 
      req.price > 0
    );
    
    if (similarRequests.length === 0) return null;
    
    let matchingRequests = similarRequests;
    
    // If we have date range, further filter for requests with the same period
    if (startDate && endDate) {
      const exactMatches = similarRequests.filter(req => 
        req.startDate === startDate && req.endDate === endDate
      );
      
      // If we have exact matches, use those, otherwise use all similar requests
      if (exactMatches.length > 0) {
        matchingRequests = exactMatches;
      }
    }
    
    // Calculate average price
    const totalPrice = matchingRequests.reduce((sum, req) => sum + (req.price || 0), 0);
    const avgPrice = totalPrice / matchingRequests.length;
    
    return {
      count: matchingRequests.length,
      avgPrice
    };
  };

  // Handle price change for all requests in group
  const handleGroupPriceChange = (groupKey: string, price: number) => {
    setRequestGroups(groups => 
      groups.map(group => 
        group.key === groupKey 
          ? { ...group, price } 
          : group
      )
    );
  };

  // Handle max approvals change
  const handleMaxApprovalsChange = (groupKey: string, value: number) => {
    setRequestGroups(groups => 
      groups.map(group => {
        if (group.key === groupKey) {
          return {
            ...group,
            maxApprovals: Math.min(Math.max(0, value), group.totalRequests)
          };
        }
        return group;
      })
    );
  };

  // Process batch approval
  const handleGroupApproval = async (group: RequestGroup) => {
    setIsProcessing(true);
    try {
      // Sort requests by creation date (oldest first)
      const sortedRequests = [...group.requests].sort(
        (a, b) => a.createdAt - b.createdAt
      );
      
      let approved = 0;
      let rejected = 0;
      
      // Process requests up to maxApprovals limit
      for (let i = 0; i < sortedRequests.length; i++) {
        const request = sortedRequests[i];
        
        if (i < group.maxApprovals) {
          // Approve request
          await approveRequest(request.id, group.price);
          approved++;
        } else {
          // Reject request
          await rejectRequest(request.id, "Request exceeded the maximum approval limit for this period");
          rejected++;
        }
      }
      
      toast.success(`Successfully approved ${approved} and rejected ${rejected} requests`);
      
      // Update the current group in the state
      setRequestGroups(prev => 
        prev.map(g => 
          g.key === group.key 
            ? { ...g, approved, totalRequests: 0, requests: [] }
            : g
        )
      );
      
      // Clear selected group
      setSelectedGroup(null);
      
    } catch (error) {
      console.error('Error processing batch approval:', error);
      toast.error('Failed to process batch approval');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Delete group (reject all requests)
  const handleRejectGroup = async (group: RequestGroup) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to reject all ${group.totalRequests} requests for ${group.hotel} during ${group.period}?`
    );
    
    if (!confirmDelete) return;
    
    setIsProcessing(true);
    try {
      for (const request of group.requests) {
        await rejectRequest(
          request.id, 
          "Batch rejection of all requests for this hotel and period"
        );
      }
      
      toast.success(`Successfully rejected all ${group.totalRequests} requests`);
      
      // Update the current group in the state
      setRequestGroups(prev => 
        prev.filter(g => g.key !== group.key)
      );
      
    } catch (error) {
      console.error('Error rejecting group:', error);
      toast.error('Failed to reject all requests in group');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.batchApproval?.title || "Batch Upgrade Approval"}
      width="90vw"
      maxWidth="900px"
    >
      <div className="space-y-6">
        {isGrouping ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
            <span className="ml-3 text-gray-600">Analyzing requests...</span>
          </div>
        ) : requestGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No pending upgrade requests found to batch process
          </div>
        ) : (
          <>
            {/* Request Group List */}
            <div className="grid gap-4 grid-cols-1">
              {requestGroups.map(group => (
                <motion.div 
                  key={group.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`border rounded-lg overflow-hidden transition-colors ${
                    selectedGroup === group.key
                      ? 'border-primary-500 shadow-md'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gray-50">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Building2 className="text-primary-500" size={20} />
                        <h3 className="font-semibold text-gray-900">{group.hotel}</h3>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-600 text-sm">
                        <Calendar size={16} />
                        <span>{group.period}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-gray-600 text-sm">
                        <ArrowUpCircle size={16} />
                        <span>
                          {group.fromRoomType} → {group.toRoomType} • {group.totalRequests} pending {group.totalRequests === 1 ? 'upgrade' : 'upgrades'}
                        </span>
                      </div>
                    </div>
                    
                    {selectedGroup !== group.key ? (
                      <button 
                        onClick={() => setSelectedGroup(group.key)}
                        className="px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
                      >
                        Configure
                      </button>
                    ) : (
                      <button 
                        onClick={() => setSelectedGroup(null)}
                        className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  
                  {selectedGroup === group.key && (
                    <div className="p-4 border-t">
                      <div className="space-y-4">
                        {/* Price Setting */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Price for Each Upgrade (SAR)
                          </label>
                          <div className="flex flex-col md:flex-row items-start gap-3">
                            <input
                              type="number"
                              value={group.price || ''}
                              onChange={(e) => handleGroupPriceChange(group.key, Number(e.target.value))}
                              className="w-full md:w-64 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                              placeholder="Enter price..."
                            />
                            
                            {/* Price suggestion info badge */}
                            {group.similarRequests ? (
                              <div className="flex items-start gap-2 bg-blue-50 px-3 py-2 rounded-lg text-blue-700 text-sm">
                                <Info className="shrink-0 mt-0.5" size={16} />
                                <div>
                                  <p>Suggested from {group.similarRequests.count} similar {group.similarRequests.count === 1 ? 'request' : 'requests'}: <span className="font-semibold">{Math.round(group.similarRequests.avgPrice)} SAR</span></p>
                                  <p className="text-xs">({group.fromRoomType} → {group.toRoomType})</p>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2 bg-yellow-50 px-3 py-2 rounded-lg text-yellow-700 text-sm">
                                <AlertCircle className="shrink-0 mt-0.5" size={16} />
                                <div>
                                  <p>No similar requests found for {group.fromRoomType} → {group.toRoomType} upgrade.</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Max Approvals Setting */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Maximum Approvals ({group.maxApprovals} of {group.totalRequests})
                          </label>
                          <div className="flex items-center">
                            <button
                              onClick={() => handleMaxApprovalsChange(group.key, group.maxApprovals - 1)}
                              disabled={group.maxApprovals <= 0}
                              className="p-2 border border-gray-300 rounded-l-lg hover:bg-gray-100 disabled:opacity-50"
                            >
                              <Minus size={16} />
                            </button>
                            <input
                              type="number"
                              value={group.maxApprovals}
                              onChange={(e) => handleMaxApprovalsChange(group.key, parseInt(e.target.value) || 0)}
                              className="w-20 px-3 py-2 border-t border-b border-gray-300 text-center"
                              min="0"
                              max={group.totalRequests}
                            />
                            <button
                              onClick={() => handleMaxApprovalsChange(group.key, group.maxApprovals + 1)}
                              disabled={group.maxApprovals >= group.totalRequests}
                              className="p-2 border border-gray-300 rounded-r-lg hover:bg-gray-100 disabled:opacity-50"
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Stats & Indicator */}
                        {group.maxApprovals < group.totalRequests && (
                          <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-700">
                            <p className="font-medium">
                              <span className="text-green-600">{group.maxApprovals}</span> requests will be approved and 
                              <span className="text-red-600"> {group.totalRequests - group.maxApprovals}</span> will be rejected
                            </p>
                            <p className="mt-1">
                              Requests will be processed in order of creation (oldest first)
                            </p>
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex justify-between pt-2">
                          <button
                            onClick={() => handleRejectGroup(group)}
                            disabled={isProcessing}
                            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 disabled:opacity-50"
                          >
                            <Trash2 size={18} />
                            <span>Reject All</span>
                          </button>
                          
                          <button
                            onClick={() => handleGroupApproval(group)}
                            disabled={isProcessing || !group.price || group.maxApprovals === 0}
                            className="flex items-center gap-2 px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? (
                              <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                <span>Processing...</span>
                              </>
                            ) : (
                              <>
                                <CheckCircle size={18} />
                                <span>Process Group</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </>
        )}
        
        {/* Bottom Actions */}
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {translations?.common?.close || "Close"}
          </button>
        </div>
      </div>
    </Modal>
  );
};
